<template>
	<div class="orders_heatmap_card">
		<div class="d-flex align-items-center justify-content-between flex-wrap mb-3">
			<h2 class="mb-0">
				<i class="fal fa-map-marked-alt mr-2"></i>
				<span class="fw-500">{{ __t('ORDERS_MAP') }}</span>
			</h2>

			<div class="period_tabs">
				<button
					v-for="p in period_options"
					:key="p.key"
					class="period_tab_btn"
					:class="{active: period === p.key}"
					@click="set_period(p.key)"
				>
					{{ __t(p.label_key) }}
				</button>
			</div>
		</div>

		<div class="heatmap_stats mb-3" v-if="!is_load">
			<div class="heatmap_stat">
				<span class="heatmap_stat__label">{{ __t('ORDERS_MAP_TOTAL') }}</span>
				<span class="heatmap_stat__value">{{ summary.total }}</span>
			</div>

			<div class="heatmap_stat">
				<span class="heatmap_stat__label">{{ __t('ORDERS_MAP_DELIVERY') }}</span>
				<span class="heatmap_stat__value color-primary-500">{{ summary.delivery_count }}</span>
			</div>

			<div class="heatmap_stat">
				<span class="heatmap_stat__label">{{ __t('ORDERS_MAP_PICKUP') }}</span>
				<span class="heatmap_stat__value color-success-500">{{ summary.pickup_count }}</span>
			</div>
		</div>

		<b-overlay :show="is_load" rounded="sm">
			<div id="orders_heatmap_map" class="orders_heatmap_map"></div>

			<div class="text-center opacity-50 p-4" v-if="!is_load && summary.total === 0">
				{{ __t('NO_DATA') }}
			</div>
		</b-overlay>
	</div>
</template>

<script>
import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";

mapboxgl.accessToken = "pk.eyJ1IjoibG9payIsImEiOiJjamh6dnF4cGwwM3JtM3BwaTg4b28zNXdhIn0.OvClW1t-Lxss_VhXQHBAwQ";

const DEFAULT_CENTER = [30.5234, 50.4501];

export default {
	name: "orders_heatmap",

	data() {
		return {
			is_load: true,
			period: 'week',

			summary: {
				total: 0,
				pickup_count: 0,
				delivery_count: 0,
			},

			geo: {
				type: 'FeatureCollection',
				features: [],
			},

			period_options: [
				{key: 'today', label_key: 'TODAY'},
				{key: 'week', label_key: 'LAST_7_DAYS'},
				{key: 'month', label_key: 'THIS_MONTH'},
				{key: 'year', label_key: 'THIS_YEAR'},
			],
		}
	},

	mounted() {
		this.$nextTick(() => {
			this.init_map();
		});
	},

	created() {
		EventBus.$on('loaded_api', this.load_stat);
	},

	beforeDestroy() {
		EventBus.$off('loaded_api', this.load_stat);

		if (this.resize_observer) {
			this.resize_observer.disconnect();
		}

		if (this.map) {
			this.map.remove();
		}
	},

	methods: {
		init_map() {
			this.map = new mapboxgl.Map({
				container: 'orders_heatmap_map',
				//light-v11 uses style-spec features the installed mapbox-gl 2.0.0
				//(package.json) can't fully render - roads/water/labels silently
				//fail to draw, leaving only the raw admin-boundary lines, and at
				//high zoom the render breaks down completely. v10 is what this
				//renderer version actually supports.
				style: 'mapbox://styles/mapbox/light-v10',
				center: DEFAULT_CENTER,
				zoom: 10,
				attributionControl: false,
			});

			//mapbox-gl 2.0.0 predates the library's own built-in container
			//ResizeObserver (added in 2.1.6), so it never notices when its
			//container changes size after init (browser zoom/DPI change,
			//sidebar toggle, sibling widgets finishing their load and shifting
			//the layout, ...). Without an explicit resize() call, the map's
			//internal canvas transform goes stale while the container's actual
			//on-screen box keeps changing.
			if (window.ResizeObserver) {
				this.resize_observer = new ResizeObserver(() => {
					if (this.map) {
						this.map.resize();
					}
				});
				this.resize_observer.observe(document.getElementById('orders_heatmap_map'));
			}

			this.map.on('load', () => {
				this.map_loaded = true;

				this.map.addSource('orders_heat', {
					type: 'geojson',
					data: this.geo,
				});

				this.map.addSource('order_counts', {
					type: 'geojson',
					data: {type: 'FeatureCollection', features: []},
				});

				this.map.addSource('order_lines', {
					type: 'geojson',
					data: {type: 'FeatureCollection', features: []},
				});

				this.map.addSource('restaurants', {
					type: 'geojson',
					data: {type: 'FeatureCollection', features: []},
				});

				//Without an explicit target, addLayer() stacks a new layer on
				//top of every existing style layer, including road/POI labels
				//and any layer still to be drawn below them. Inserting ours
				//right before the style's first symbol (label) layer keeps
				//the order points/connector line under all text labels, same
				//as they'd be if they were authored into the style itself.
				const first_symbol_layer = (this.map.getStyle().layers || [])
					.find(layer => layer.type === 'symbol');
				const before_id = first_symbol_layer ? first_symbol_layer.id : undefined;

				this.map.addLayer({
					id: 'orders-heat-layer',
					type: 'heatmap',
					source: 'orders_heat',
					paint: {
						'heatmap-weight': 1,
						'heatmap-intensity': ['interpolate', ['linear'], ['zoom'], 0, 1, 15, 3],
						'heatmap-color': [
							'interpolate', ['linear'], ['heatmap-density'],
							0, 'rgba(0,0,0,0)',
							0.2, 'rgb(0,200,0)',
							0.4, 'rgb(173,255,47)',
							0.6, 'rgb(255,230,0)',
							0.8, 'rgb(255,140,0)',
							1, 'rgb(220,20,20)'
						],
						'heatmap-radius': ['interpolate', ['linear'], ['zoom'], 0, 2, 15, 25],
						'heatmap-opacity': 0.85,
					},
				}, before_id);

				//Number of orders sharing the exact same coordinates (mostly
				//pickup orders at the same restaurant) - the heatmap blends
				//overlapping points into a hotter color, but doesn't show the
				//actual count. A solid badge behind the number, independent
				//of the heatmap's own (often similarly red/dark) coloring
				//underneath, so it stays legible regardless of density color.
				this.map.addLayer({
					id: 'order-counts-badge-layer',
					type: 'circle',
					source: 'order_counts',
					filter: ['>', ['get', 'count'], 1],
					paint: {
						'circle-radius': ['interpolate', ['linear'], ['get', 'count'], 2, 10, 50, 16],
						'circle-color': '#3b3f5c',
						'circle-stroke-width': 2,
						'circle-stroke-color': '#fff',
					},
				});

				this.map.addLayer({
					id: 'order-counts-layer',
					type: 'symbol',
					source: 'order_counts',
					filter: ['>', ['get', 'count'], 1],
					layout: {
						'text-field': ['get', 'count'],
						'text-size': 12,
						'text-allow-overlap': true,
					},
					paint: {
						'text-color': '#fff',
					},
				});

				//Added after the order points layer (with the same before_id)
				//so it stacks visually above the points but still under labels.
				this.map.addLayer({
					id: 'order-lines-layer',
					type: 'line',
					source: 'order_lines',
					paint: {
						'line-color': '#4f5bd5',
						'line-width': 2,
						'line-opacity': 0.9,
						'line-dasharray': [2, 2],
					},
				}, before_id);

				//Restaurant pins as native GL layers (circle + text), not
				//mapboxgl.Marker DOM elements. Up to 200 restaurants come
				//back from load_restaurants(); each mapboxgl.Marker is a
				//separate DOM node promoted to its own GPU-composited layer
				//(will-change: transform), and that many of them was
				//confirmed live (across a desktop browser and an iPhone) to
				//corrupt the map's WebGL canvas into a stray dashed mesh
				//over every road/building - independent of which markers
				//were actually visible. A GL layer draws all of them in the
				//same canvas pass, so the count no longer matters.
				this.map.addLayer({
					id: 'restaurants-circle-layer',
					type: 'circle',
					source: 'restaurants',
					paint: {
						'circle-radius': 6,
						'circle-color': '#3b3f5c',
						'circle-stroke-width': 2,
						'circle-stroke-color': '#fff',
					},
				}, before_id);

				this.map.addLayer({
					id: 'restaurants-label-layer',
					type: 'symbol',
					source: 'restaurants',
					layout: {
						'text-field': ['get', 'title'],
						'text-size': 11,
						'text-offset': [0, 1.2],
						'text-anchor': 'top',
					},
					paint: {
						'text-color': '#3b3f5c',
						'text-halo-color': '#fff',
						'text-halo-width': 1.5,
					},
				});

				this.render_geo();
				this.load_restaurants();
			});

			if (window.hasOwnProperty("api")) {
				this.load_stat();
			}
		},

		set_period(key) {
			this.period = key;
			this.load_stat();
		},

		async load_restaurants() {
			await api.post('/internal/v1/catalog/restaurants', {
				filter: {},
				list: {limit: 200}
			}).then(({data}) => {
				if (data.status === "operation_success") {
					this.render_restaurants(data.data || []);
				}
			}).catch(() => {
			});
		},

		render_restaurants(restaurants) {
			if (!this.map_loaded || !this.map.getSource('restaurants')) {
				return;
			}

			const features = [];

			restaurants.forEach(rest => {
				const lat = parseFloat(rest.lat);
				const lng = parseFloat(rest.lng);

				if (!lat || !lng) {
					return;
				}

				features.push({
					type: 'Feature',
					properties: {title: rest.title || ''},
					geometry: {type: 'Point', coordinates: [lng, lat]},
				});
			});

			this.map.getSource('restaurants').setData({type: 'FeatureCollection', features});
		},

		render_connections() {
			if (!this.map_loaded || !this.map.getSource('order_lines')) {
				return;
			}

			const features = [];

			this.geo.features.forEach(feature => {
				const rest_coord = feature.properties && feature.properties.restaurant_coordinates;

				if (!rest_coord) {
					return;
				}

				const [olng, olat] = feature.geometry.coordinates;
				const [rlng, rlat] = rest_coord;

				//pickup orders are placed at the restaurant itself, so their point
				//already equals the restaurant's coordinates - skip the zero-length line.
				if (olng === rlng && olat === rlat) {
					return;
				}

				features.push({
					type: 'Feature',
					properties: {},
					geometry: {
						type: 'LineString',
						coordinates: [feature.geometry.coordinates, rest_coord],
					},
				});
			});

			this.map.getSource('order_lines').setData({type: 'FeatureCollection', features});
		},

		async load_stat() {
			this.is_load = true;

			await api.post('/internal/v1/catalog/dashboard/orders-map/' + this.period, {}).then(({data}) => {
				this.is_load = false;
				if (data.status === "operation_success") {
					this.summary = {
						total: data.data.total || 0,
						pickup_count: data.data.pickup_count || 0,
						delivery_count: data.data.delivery_count || 0,
					};
					this.geo = data.data.geo || {type: 'FeatureCollection', features: []};
					this.render_geo();
				}
			}).catch(err => {
				this.is_load = false;
				EventBus.$emit('show_toast', {err: err.message, type: "danger", title: err.name})
			});
		},

		render_geo() {
			if (!this.map_loaded || !this.map.getSource('orders_heat')) {
				return;
			}

			this.map.getSource('orders_heat').setData(this.geo);
			this.fit_map_bounds();
			this.render_connections();
			this.render_order_counts();
		},

		//Groups order points by exact coordinate match and shows the count
		//as a text badge - the heatmap layer blends overlapping orders into
		//a hotter color, but doesn't say how many there actually are.
		render_order_counts() {
			if (!this.map_loaded || !this.map.getSource('order_counts')) {
				return;
			}

			const counts = new Map();

			this.geo.features.forEach(feature => {
				const [lng, lat] = feature.geometry.coordinates;
				const key = lng + ',' + lat;
				counts.set(key, (counts.get(key) || 0) + 1);
			});

			const features = [];

			counts.forEach((count, key) => {
				const [lng, lat] = key.split(',').map(Number);
				features.push({
					type: 'Feature',
					properties: {count},
					geometry: {type: 'Point', coordinates: [lng, lat]},
				});
			});

			this.map.getSource('order_counts').setData({type: 'FeatureCollection', features});
		},

		//Auto-fits/centers the map over the order heat points, plus the
		//restaurant coordinate attached to each order (if any). The full
		//unfiltered restaurant list (up to 200, shown for reference) is
		//deliberately excluded - including all of it used to blow up the
		//bounds to the client's whole restaurant footprint and made the map
		//jump/re-zoom as soon as it finished loading. But excluding restaurant
		//coordinates entirely caused a different bug: with few/no orders the
		//map would zoom in tight on just the order point(s), panning the
		//(still-rendered, still on the map) restaurant pins out of the visible
		//viewport - which looks identical to the markers having disappeared.
		fit_map_bounds() {
			if (!this.map_loaded) {
				return;
			}

			const all_coords = [];

			this.geo.features.forEach(feature => {
				all_coords.push(feature.geometry.coordinates);

				const rest_coord = feature.properties && feature.properties.restaurant_coordinates;
				if (rest_coord) {
					all_coords.push(rest_coord);
				}
			});

			if (all_coords.length === 0) {
				return;
			}

			const bounds = new mapboxgl.LngLatBounds();
			all_coords.forEach(coord => bounds.extend(coord));

			const ne = bounds.getNorthEast();
			const sw = bounds.getSouthWest();
			const is_tiny_area = Math.abs(ne.lng - sw.lng) < 0.01 && Math.abs(ne.lat - sw.lat) < 0.01;

			if (is_tiny_area) {
				this.map.easeTo({center: bounds.getCenter(), zoom: 12});
			} else {
				this.map.fitBounds(bounds, {padding: 60, maxZoom: 13});
			}
		},
	},
}
</script>

<style scoped>
.orders_heatmap_card {
	background: #fff;
	border: 1px solid #eef0f5;
	border-radius: 12px;
	padding: 16px 18px;
	margin-bottom: 1.5rem;
}

.orders_heatmap_map {
	width: 100%;
	height: 420px;
	border-radius: 10px;
	overflow: hidden;
}

.period_tabs {
	display: flex;
	gap: 6px;
	flex-wrap: wrap;
}

.period_tab_btn {
	border: 1px solid #eef0f5;
	background: #fff;
	border-radius: 999px;
	padding: 5px 14px;
	font-size: .8rem;
	color: #6b7280;
	transition: all .15s ease;
}

.period_tab_btn.active {
	background: #3b3f5c;
	border-color: #3b3f5c;
	color: #fff;
}

.heatmap_stats {
	display: flex;
	gap: 12px;
	flex-wrap: wrap;
}

.heatmap_stat {
	display: flex;
	flex-direction: column;
	padding: 10px 16px;
	border: 1px solid #eef0f5;
	border-radius: 10px;
	min-width: 120px;
}

.heatmap_stat__label {
	font-size: .72rem;
	opacity: .6;
}

.heatmap_stat__value {
	font-size: 1.2rem;
	font-weight: 600;
}
</style>
